import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { listDocumentDrafts } from '../lib/documentDraft';

const HIDE_LEAVE_KEY = 'zb_hide_leave_warning';

const DraftGuardContext = createContext(null);

export function useDraftGuard() {
  const ctx = useContext(DraftGuardContext);
  // Tolerate usage outside the provider (e.g. isolated tests) by returning no-ops.
  return ctx || {
    markDirty: () => {}, markClean: () => {}, attemptNavigate: () => {},
    drafts: [], refreshDrafts: () => {},
  };
}

export function DraftGuardProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Is the currently-mounted create page holding unsaved data worth warning about?
  const dirtyRef = useRef(false);
  // Target path awaiting user's confirmation in the leave modal.
  const [pendingTarget, setPendingTarget] = useState(null);
  // Parked drafts for the signed-in user (drives the notification bell).
  const [drafts, setDrafts] = useState([]);
  const [userId, setUserId] = useState(null);
  // id -> name for the user's clients, so the bell can label parked drafts that
  // stored only a client id (older drafts, or ones parked before the name was
  // captured). A ref keeps the latest map available to refreshDrafts without
  // making it a dependency.
  const clientNamesRef = useRef(null);

  const refreshDrafts = useCallback((uid = userId) => {
    if (!uid) { setDrafts([]); return; }
    setDrafts(listDocumentDrafts(uid, clientNamesRef.current));
  }, [userId]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      const uid = data?.user?.id || null;
      setUserId(uid);
      if (uid) setDrafts(listDocumentDrafts(uid, clientNamesRef.current));
    });
  }, []);

  // Load the client id -> name map once we know the user, then re-scan drafts so
  // any name-less parked entries pick up their client label.
  useEffect(() => {
    if (!userId) { clientNamesRef.current = null; return; }
    let cancelled = false;
    supabase.from('clients').select('id, name').then(({ data }) => {
      if (cancelled || !data) return;
      const map = {};
      for (const c of data) map[c.id] = c.name;
      clientNamesRef.current = map;
      setDrafts(listDocumentDrafts(userId, map));
    });
    return () => { cancelled = true; };
  }, [userId]);

  // Re-scan parked drafts whenever the route changes (covers save→redirect,
  // returning to the bell, etc.) so the badge count stays accurate.
  useEffect(() => {
    if (userId) setDrafts(listDocumentDrafts(userId, clientNamesRef.current));
  }, [location.pathname, location.search, userId]);

  const markDirty = useCallback(() => { dirtyRef.current = true; }, []);
  const markClean = useCallback(() => { dirtyRef.current = false; }, []);

  const leaveWarningSuppressed = () => {
    try { return localStorage.getItem(HIDE_LEAVE_KEY) === '1'; } catch { return false; }
  };

  // Single navigation primitive used by guarded links / buttons. If a dirty form
  // is mounted and the warning isn't suppressed, intercept and show the modal;
  // otherwise navigate straight away.
  const attemptNavigate = useCallback((to) => {
    if (dirtyRef.current && !leaveWarningSuppressed()) {
      setPendingTarget(to);
    } else {
      navigate(to);
    }
  }, [navigate]);

  const confirmLeave = useCallback((dontShowAgain) => {
    if (dontShowAgain) {
      try { localStorage.setItem(HIDE_LEAVE_KEY, '1'); } catch { /* ignore */ }
    }
    const to = pendingTarget;
    setPendingTarget(null);
    dirtyRef.current = false;        // the create page is about to unmount
    if (to != null) navigate(to);
  }, [pendingTarget, navigate]);

  const cancelLeave = useCallback(() => setPendingTarget(null), []);

  return (
    <DraftGuardContext.Provider value={{ markDirty, markClean, attemptNavigate, drafts, refreshDrafts }}>
      {children}
      {pendingTarget != null && (
        <LeaveModal t={t} onCancel={cancelLeave} onConfirm={confirmLeave} />
      )}
    </DraftGuardContext.Provider>
  );
}

// Panel 5: "Vas a salir de un formulario en progreso…"
function LeaveModal({ t, onCancel, onConfirm }) {
  const [dontShow, setDontShow] = useState(false);
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-lg font-bold text-[#1d2d3d] border-b border-[#e1e5e8] pb-3">
          {t('draftGuard.leaveTitle')}
        </h3>
        <p className="text-sm text-[#6d7d8b] mt-4 leading-relaxed">
          {t('draftGuard.leaveBody')}
        </p>
        <label className="flex items-center gap-2.5 mt-5 cursor-pointer">
          <input
            type="checkbox"
            checked={dontShow}
            onChange={(e) => setDontShow(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-[#1a4f8b] focus:ring-[#1a4f8b]"
          />
          <span className="text-sm text-[#6d7d8b]">{t('draftGuard.dontShowAgain')}</span>
        </label>
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onCancel}
            className="px-5 h-11 rounded-full border border-[#d6dce2] text-[#1a4f8b] font-semibold text-sm hover:bg-gray-50 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={() => onConfirm(dontShow)}
            className="px-6 h-11 rounded-full bg-[#fca311] hover:bg-[#e0920f] text-white font-bold text-sm transition-colors"
          >
            {t('draftGuard.understood')}
          </button>
        </div>
      </div>
    </div>
  );
}
